import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { FiArrowLeft, FiPackage, FiCheckCircle } from "react-icons/fi";
import { API_BASE, WHATSAPP_NUMBER } from "../config";
import Loader from "../components/admin/Loader";
import "../styles/product-detail.css";

const FALLBACK_SPECS = {
  "1": {
    _id: "1",
    name: "Basmati Rice & Cereals",
    category: "Export",
    description: "Premium-grade rice and cereal grains sourced for bulk export to Middle East and Europe.",
    hsnCode: "1006.30.20",
    varieties: "1121 XXL, Pusa Basmati, Sugandha, Sharbati",
    importCategory: "Agro Commodities",
    countryOfOrigin: "India",
    packaging: "25kg / 50kg Non-Woven / Jute / PP Bags",
    shelfLife: "24 Months",
    mandatoryCertifications: "FSSAI, APEDA, Phytosanitary Certificate",
    indianCompliance: "DGFT Registered, AGMARK Certified",
    freightix1012Role: "Procurement, Quality Inspection, Customs & Freight Forwarding",
    image: "",
  },
  "2": {
    _id: "2",
    name: "Handmade Textiles",
    category: "Export",
    description: "Traditional and modern textile goods, sourced from verified manufacturers across India.",
    hsnCode: "5208.11.00",
    varieties: "Cotton, Silk, Linen, Handloom Fabrics",
    importCategory: "Textiles & Apparel",
    countryOfOrigin: "India",
    packaging: "Rolls in polybags and corrugated export cartons",
    shelfLife: "N/A",
    mandatoryCertifications: "Texprocil, ISO 9001, OEKO-TEX",
    indianCompliance: "Textiles Committee Cleared, GST Compliant",
    freightix1012Role: "Consolidation, Port Clearance & Sea Freight",
    image: "",
  },
  "3": {
    _id: "3",
    name: "Industrial Machinery",
    category: "Export",
    description: "Machinery parts and equipment for industrial buyers in Africa and Southeast Asia.",
    hsnCode: "8479.89.99",
    varieties: "CNC Parts, Milling Accessories, Spare Gears",
    importCategory: "Capital Goods & Machinery",
    countryOfOrigin: "India",
    packaging: "ISPM-15 treated wooden crating with vacuum foil",
    shelfLife: "N/A",
    mandatoryCertifications: "CE, ISO 9001:2015",
    indianCompliance: "EPCG Scheme & Pre-shipment Inspection",
    freightix1012Role: "Heavy Lift Logistics, Custom Crating & Door Delivery",
    image: "",
  },
  "4": {
    _id: "4",
    name: "Spices & Condiments",
    category: "Export",
    description: "Bulk spices and condiments, quality-checked before shipment to global markets.",
    hsnCode: "0910.30.00",
    varieties: "Salem Turmeric, Guntur Red Chilli, Black Pepper 550GL",
    importCategory: "Agro & Food Commodities",
    countryOfOrigin: "India",
    packaging: "25kg multi-wall paper bags with PE inner liner",
    shelfLife: "18 Months",
    mandatoryCertifications: "Spices Board of India, FSSAI, US FDA Registered",
    indianCompliance: "Spice Board EIC Inspection & Lab Testing",
    freightix1012Role: "Batch Testing, Fumigation, Port Clearance & Cold-chain Booking",
    image: "",
  },
  "5": {
    _id: "5",
    name: "Electronic Components",
    category: "Import",
    description: "Sourced electronic parts and components for local distribution and assembly.",
    hsnCode: "8542.31.00",
    varieties: "SMD Capacitors, Microcontrollers, PCB Assemblies",
    importCategory: "Electronics & Semiconductors",
    countryOfOrigin: "Taiwan / South Korea",
    packaging: "Antistatic reel & tray packaging in sealed moisture bags",
    shelfLife: "36 Months",
    mandatoryCertifications: "BIS CRS, RoHS, CE",
    indianCompliance: "Customs Faceless Assessment & BIS Clearance",
    freightix1012Role: "Air Cargo Handling, Customs Bonded Clearance & First-Mile Pickup",
    image: "",
  },
  "6": {
    _id: "6",
    name: "Auto Spare Parts",
    category: "Import",
    description: "Genuine and aftermarket auto parts, imported and inspected for quality.",
    hsnCode: "8708.29.00",
    varieties: "Brake Pads, Suspensions, Engine Gaskets",
    importCategory: "Automotive Components",
    countryOfOrigin: "Germany / Japan",
    packaging: "Boxed OEM units on shrink-wrapped Euro pallets",
    shelfLife: "N/A",
    mandatoryCertifications: "ARAI / ICAT Certificate, ISO/TS 16949",
    indianCompliance: "AIS Compliance, Customs Tariff Chapter 87",
    freightix1012Role: "Container De-stuffing, Customs Clearance & Domestic Dispatch",
    image: "",
  },
};

function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setLoading(true);
    setError("");

    fetch(`${API_BASE}/products/${id}`)
      .then(async (res) => {
        if (!res.ok) {
          if (FALLBACK_SPECS[id]) {
            return FALLBACK_SPECS[id];
          }
          throw new Error("Product not found");
        }
        return res.json();
      })
      .then((data) => {
        setProduct(data);
        if (data && data.name) {
          document.title = `${data.name} | KrivaExim`;
        }
      })
      .catch((err) => {
        if (FALLBACK_SPECS[id]) {
          setProduct(FALLBACK_SPECS[id]);
          document.title = `${FALLBACK_SPECS[id].name} | KrivaExim`;
        } else {
          setError(err.message || "Failed to load product");
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main className="product-detail-page">
        <Loader text="Loading product details..." />
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="product-detail-page">
        <div className="detail-state-card">
          <h2>Product Not Found</h2>
          <p>{error || "The requested product does not exist or has been removed."}</p>
          <Link to="/products" className="btn-primary">
            ← Back to Products
          </Link>
        </div>
      </main>
    );
  }

  const isVideo = product.image && /\.(mp4|webm|mov)(\?|$)/i.test(product.image);
  const message = product.hsnCode
    ? `Hi, I'm interested in ${product.name} (HSN: ${product.hsnCode}). Can you share pricing and shipment details?`
    : `Hi, I'm interested in ${product.name}. Can you share pricing and shipment details?`;
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

  const handleInquiry = () => {
    fetch(`${API_BASE}/analytics/inquiry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product: product.name }),
    }).catch(() => {});
  };

  // Specifications table rows
  const specRows = [
    { label: "HSN Code", value: product.hsnCode, highlight: true },
    { label: "Varieties", value: product.varieties },
    { label: "Import Category", value: product.importCategory },
    { label: "Country of Origin", value: product.countryOfOrigin },
    { label: "Packaging", value: product.packaging },
    { label: "Shelf Life", value: product.shelfLife },
    { label: "Mandatory Certifications", value: product.mandatoryCertifications },
    { label: "Indian Compliance", value: product.indianCompliance },
    { label: "Freightix1012 Role", value: product.freightix1012Role, highlight: true },
  ];

  return (
    <main className="product-detail-page">
      <Link to="/products" className="detail-back-link">
        <FiArrowLeft size={16} /> Back to Products
      </Link>

      <section className="product-detail-hero">
        <div className="product-detail-media">
          {product.image ? (
            isVideo ? (
              <video src={product.image} controls />
            ) : (
              <img src={product.image} alt={product.name} />
            )
          ) : (
            <div className="product-detail-media-empty">
              <FiPackage size={48} />
              <span>No image available</span>
            </div>
          )}
        </div>

        <div className="product-detail-info">
          <span className={`product-type-badge ${product.category?.toLowerCase()}`}>
            {product.category || "Trade"}
          </span>

          <h1 className="product-detail-title">{product.name}</h1>
          <p className="product-detail-desc">{product.description}</p>

          <div className="product-detail-highlights">
            {product.hsnCode && (
              <span className="highlight-chip">
                <strong>HSN:</strong> {product.hsnCode}
              </span>
            )}
            {product.countryOfOrigin && (
              <span className="highlight-chip">
                <strong>Origin:</strong> {product.countryOfOrigin}
              </span>
            )}
            {product.shelfLife && (
              <span className="highlight-chip">
                <strong>Shelf Life:</strong> {product.shelfLife}
              </span>
            )}
          </div>

          <div className="detail-actions">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp-large"
              onClick={handleInquiry}
            >
              <FaWhatsapp size={22} />
              <span>Enquire on WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="product-specs-section">
        <div className="specs-section-header">
          <h2>Product Specifications &amp; Compliance Details</h2>
          <p>Verified trade parameters, export standards, packaging, and regulatory requirements.</p>
        </div>

        <div className="specs-table-wrapper">
          <table className="specs-table">
            <thead>
              <tr>
                <th>Specification</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {specRows.map((row, idx) => (
                <tr key={idx} className={row.highlight ? "specs-table-highlight" : ""}>
                  <td className="specs-table-key">{row.label}</td>
                  <td className="specs-table-val">
                    {row.value || <span style={{ color: "#a0aec0", fontStyle: "italic" }}>Available on request</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="detail-assistance-box">
          <div className="detail-assistance-text">
            <h4><FiCheckCircle size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Custom Requirements &amp; Bulk Orders</h4>
            <p>Need customized packaging, specific port of delivery, or lab testing reports? Our desk coordinates the entire trade workflow.</p>
          </div>
          <Link to="/contact" className="btn-secondary">
            Request Custom Quote
          </Link>
        </div>
      </section>
    </main>
  );
}

export default ProductDetail;
